<template>
  <div class="crm_file_upload_field">
    <div v-if="displayFiles.length > 0" class="file_list_container mb-2">
      <VueDraggableNext
        v-model="localFiles"
        :disabled="disabled || isImageProperty"
        handle=".drag_handle"
        ghost-class="file_ghost"
        @end="handleDragEnd"
      >
        <CrmFileItem
          v-for="file in displayFiles"
          :key="file.id"
          :file="file"
          :disabled="disabled"
          :hide-drag-handle="isImageProperty"
          @download="handleDownload"
          @delete="handleDelete"
        />
      </VueDraggableNext>
    </div>

    <div v-if="!disabled" class="add_file_container">
      <input
        type="file"
        ref="fileInputRef"
        :multiple="false"
        :accept="acceptAttr"
        class="hidden"
        style="
          display: none !important;
          visibility: hidden !important;
          position: absolute !important;
          width: 0 !important;
          height: 0 !important;
        "
        @change="handleFileSelect"
      />
      <a-button
        class="secondary_small"
        :loading="isUploading"
        @click="triggerFileInput"
      >
        <template #icon>
          <ThemeImage :icon="uploadButtonIcon" class="size-4" />
        </template>
        <span class="text-sm font-semibold leading-[17px] align-middle">{{
          uploadButtonLabel
        }}</span>
      </a-button>
    </div>

    <div
      v-if="disabled && localFiles.length === 0"
      class="text-sm secondary_color py-2"
    >
      No files attached
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { imageNames } from "@/src/constants/imageConstants.js";
import { useCrmFileUpload } from "@/src/composables/useCrmFileUpload.js";
import {
  ATTRIBUTE_TYPES,
  IMAGE_ALLOWED_MIME_TYPES,
  IMAGE_ACCEPT_ATTR,
  getServiceForSlug,
} from "@/src/constants/crmConstants.js";
import { useStore } from "vuex";
import {
  updateStandardObjectField,
  updateCustomObjectField,
} from "@/src/utils/kanbanUpdateUtils.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CrmFileItem from "./CrmFileItem.vue";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  property: {
    type: Object,
    required: true,
  },
  objectSlug: {
    type: String,
    default: null,
  },
  recordId: {
    type: [String, Number],
    default: null,
  },
  isStandardObject: {
    type: Boolean,
    default: false,
  },
  standardType: {
    type: String,
    default: null,
  },
  schemaProperties: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "files-changed"]);

const store = useStore();
const fileInputRef = ref(null);
const localFiles = ref([]);

const isImageProperty = computed(
  () =>
    String(props.property?.type || "").toLowerCase() === ATTRIBUTE_TYPES.IMAGE,
);
const acceptAttr = computed(() =>
  isImageProperty.value ? IMAGE_ACCEPT_ATTR : undefined,
);
const allowedMimeTypes = computed(() =>
  isImageProperty.value ? IMAGE_ALLOWED_MIME_TYPES : null,
);

const displayFiles = computed(() =>
  isImageProperty.value ? localFiles.value.slice(0, 1) : localFiles.value,
);

const uploadButtonLabel = computed(() => {
  if (!isImageProperty.value) return "Add file or media";
  return displayFiles.value.length > 0 ? "Replace Image" : "Upload Image";
});

const uploadButtonIcon = computed(() => {
  if (isImageProperty.value && displayFiles.value.length > 0) {
    return imageNames.UPLOAD_GREY;
  }
  return imageNames.PLUS;
});

const {
  files,
  isUploading,
  initializeFiles,
  uploadSingleFile,
  downloadFile,
  deleteFile,
  getFilesForPayload,
} = useCrmFileUpload();

let isInitializing = false;
let lastPayloadJson = "";

watch(
  () => props.modelValue,
  (newValue) => {
    const isArrayFormat = Array.isArray(newValue);
    const isNestedFormat =
      newValue && typeof newValue === "object" && Array.isArray(newValue.files);

    if (!isArrayFormat && !isNestedFormat) {
      return;
    }

    if (isUploading.value) return;

    const filesToInit = isArrayFormat ? newValue : newValue.files;

    isInitializing = true;
    initializeFiles(filesToInit);
    localFiles.value = [...files.value];
    lastPayloadJson = JSON.stringify(getFilesForPayload());
    isInitializing = false;
  },
  { immediate: true, deep: true },
);

const saveRecord = async (payload) => {
  if (!props.recordId) {
    return;
  }

  const newValue = payload.length > 0 ? payload : null;

  try {
    if (props.isStandardObject && props.standardType) {
      const result = await updateStandardObjectField({
        standardType: props.standardType,
        recordId: props.recordId,
        fieldSlug: props.property.slug,
        newValue,
        schemaProperties: props.schemaProperties,
      });
    } else if (props.objectSlug) {
      const result = await updateCustomObjectField({
        slug: props.objectSlug,
        recordId: props.recordId,
        fieldSlug: props.property.slug,
        newValue,
        service: getServiceForSlug(props.objectSlug, store),
      });
    } else {
    }
  } catch (error) {}
};

watch(
  () => files.value,
  (newFiles) => {
    localFiles.value = [...newFiles];

    if (!isInitializing) {
      const payload = getFilesForPayload();
      const payloadJson = JSON.stringify(payload);

      if (payloadJson !== lastPayloadJson) {
        lastPayloadJson = payloadJson;
        emit("update:modelValue", payload);
        emit("files-changed", payload);

        if (props.recordId && !isUploading.value) {
          saveRecord(payload);
        }
      }
    }
  },
  { deep: true },
);

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const handleFileSelect = async (event) => {
  const selectedFiles = event.target.files;
  if (!selectedFiles || selectedFiles.length === 0) return;

  if (isImageProperty.value) {
    files.value = [];
  }

  for (const file of Array.from(selectedFiles)) {
    uploadSingleFile(file, { allowedMimeTypes: allowedMimeTypes.value });
  }

  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

const handleDownload = (file) => {
  downloadFile(file);
};

const handleDelete = async (file) => {
  try {
    await deleteFile({
      file,
      propertySlug: props.property.slug,
      objectSlug: props.objectSlug,
      recordId: props.recordId,
      isStandardObject: props.isStandardObject,
      standardType: props.standardType,
      schemaProperties: props.schemaProperties,
      service: getServiceForSlug(props.objectSlug, store),
    });
  } catch (error) {}
};

const handleDragEnd = () => {
  files.value = [...localFiles.value];
};
</script>

<style scoped>
.crm_file_upload_field {
  width: 100%;
}

.file_list_container {
  border-radius: 8px;
}

.file_ghost {
  opacity: 0.5;
  background: var(--white-opacity-8);
  border-radius: 8px;
}

.add_file_container {
  display: flex;
  align-items: center;
}
</style>
